import apiClient from '@/api'

export type IamDirectoryUser = {
  id: number | string | null
  employee_code: string | null
  name: string | null
  email: string | null
  username: string | null
  is_active: boolean | null
}

export type DraftAllowlistUser = {
  id: number
  employeeCode: string
  displayName: string | null
  plantCode: string | null
  isActive: boolean
  notes: string | null
  createdBy: string | null
  updatedBy: string | null
  createdAt?: string
  updatedAt?: string | null
}

function normalizeDraftUser(raw: Record<string, unknown>): DraftAllowlistUser {
  return {
    id: Number(raw.id),
    employeeCode: String(raw.employeeCode ?? raw.employee_code ?? ''),
    displayName: (raw.displayName ?? raw.display_name ?? null) as string | null,
    plantCode: (raw.plantCode ?? raw.plant_code ?? null) as string | null,
    isActive: Boolean(raw.isActive ?? raw.is_active ?? true),
    notes: (raw.notes ?? null) as string | null,
    createdBy: (raw.createdBy ?? raw.created_by ?? null) as string | null,
    updatedBy: (raw.updatedBy ?? raw.updated_by ?? null) as string | null,
    createdAt: (raw.createdAt ?? raw.created_at) as string | undefined,
    updatedAt: (raw.updatedAt ?? raw.updated_at ?? null) as string | null | undefined,
  }
}

export const draftUsersService = {
  /** Read-only IAM directory via RFID BE proxy — never writes to IAM. */
  async listIamUsers(): Promise<IamDirectoryUser[]> {
    const { data } = await apiClient.get<{ data: IamDirectoryUser[] }>('/admin/iam_users')
    return data.data ?? []
  },

  async listAllowlist(): Promise<DraftAllowlistUser[]> {
    const { data } = await apiClient.get<{ data: Record<string, unknown>[] }>(
      '/admin/custout_draft_users'
    )
    return (data.data ?? []).map((row) => normalizeDraftUser(row))
  },

  async addToAllowlist(payload: {
    employee_code: string
    display_name?: string
    plant_code?: string
    notes?: string
    actor?: string
  }): Promise<DraftAllowlistUser> {
    const { data } = await apiClient.post<{ data: Record<string, unknown> }>(
      '/admin/custout_draft_users',
      payload
    )
    return normalizeDraftUser(data.data)
  },

  async updateAllowlistUser(
    id: number,
    payload: {
      display_name?: string
      plant_code?: string
      notes?: string
      is_active?: boolean
      actor?: string
    }
  ): Promise<DraftAllowlistUser> {
    const { data } = await apiClient.patch<{ data: Record<string, unknown> }>(
      `/admin/custout_draft_users/${id}`,
      payload
    )
    return normalizeDraftUser(data.data)
  },

  /** Remove from local allowlist only — does not modify IAM. */
  async removeFromAllowlist(id: number): Promise<void> {
    await apiClient.delete(`/admin/custout_draft_users/${id}`)
  },
}
